import apiService from "@/lib/api";

const TENANT_SETTINGS_BASE = "/api/v2/tenant-settings";
const ADMIN_BASE = "/api/v2/admin";

export const TenantSettingsService = {
    getSettings: async () => {
        try {
            const response = await apiService.getSecure(TENANT_SETTINGS_BASE);
            return response.data;
        } catch (error) {
            console.error("Error while fetching tenant settings:", error);
            throw error;
        }
    },

    /**
     * Unauthenticated read for the candidate-facing career portal, which has
     * no token to send — `get`, not `getSecure`. Serves `requireApplyEmailConsent`,
     * which words the apply flow's consent step.
     *
     * The response is camelCase: PublicTenantSettingsData aliases
     * AppBaseCamelCaseModel as AppBaseModel, unlike most schemas here.
     */
    getPublicSettings: async () => {
        try {
            const response = await apiService.get(`${TENANT_SETTINGS_BASE}/public`);
            return response.data;
        } catch (error) {
            console.error("Error while fetching public tenant settings:", error);
            throw error;
        }
    },

    /**
     * Partial update — send ONLY the settings being changed. Omitted keys are
     * left untouched server-side, so resending the whole object would clobber
     * a change another admin made between this client's read and its write.
     * An empty body is rejected with NO_SETTINGS_PROVIDED; an unknown key 422s.
     *
     * The response is always the full post-write state, so all toggles can be
     * driven off it.
     *
     * @param {Object} payload e.g. `{ requireBulkEmailConsent: false }`
     */
    updateSettings: async (payload) => {
        try {
            const response = await apiService.patch(TENANT_SETTINGS_BASE, payload);
            return response.data;
        } catch (error) {
            console.error("Error while updating tenant settings:", error);
            throw error;
        }
    },

    /**
     * Unauthenticated read for the pages that render before anyone signs in:
     * the career portal's consent wording and the login page's sign-in method.
     * `get`, not `getSecure` — there is no token on the login page, and the
     * tenant is selected by the `X-Tenant-Slug` header the interceptor sends.
     *
     * `data.adLoginEnabled` is the EFFECTIVE answer: the tenant's flag ANDed
     * with the deployment's corporate-login switch, so the form the page
     * offers is exactly the one `/auth/login` will accept.
     */
    getPublicSettings: async () => {
        try {
            const response = await apiService.get(`${TENANT_SETTINGS_BASE}/public`);
            return response.data;
        } catch (error) {
            console.error("Error while fetching public tenant settings:", error);
            throw error;
        }
    },

    getDefaultCurrency: async () => {
        try {
            const response = await apiService.get(`${ADMIN_BASE}/default-currency`);
            const data = response?.data?.data ?? response?.data ?? {};
            return {
                currency: data.currency || "",
                currencySign: data.currencySign ?? data.currency_sign ?? "",
            };
        } catch (error) {
            console.error("Error while fetching default currency:", error);
            throw error;
        }
    },

    updateDefaultCurrency: async (payload) => {
        try {
            const response = await apiService.patch(`${ADMIN_BASE}/update-default-currency`, payload);
            return response?.data?.data ?? response?.data ?? {};
        } catch (error) {
            console.error("Error while updating default currency:", error);
            throw error;
        }
    },

};

export default TenantSettingsService;
